import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Story, StoryImage } from "../../../services/restserver-openapi";
import {
    addStory,
    getStoriesByTourId,
    deleteStoryById,
    modifyStory,
    getStoryUploadLink,
    confirmStoryImageUpload, getStoriesByCompundId
} from "../../../utils/Vanlifer/community";
import { mapErrorMessages } from "../../../utils/errorHandling";
import { AppDispatch } from "../../store";
import axios from 'axios';
import imageCompression from "browser-image-compression";

interface StoryState {
    stories: Story[];
    nextCursorId?: number;
    nextCursorDate?: number;
    loading: boolean;
    error?: string;
    uploads: {
        [localId: string]: {
            status: 'compressing' | 'uploading' | 'success' | 'error';
            errorMessage?: string;
            fileExtension: string;
            remoteName?: string;
            image?: StoryImage;
        };
    };
}

const initialState: StoryState = {
    stories: [],
    loading: false,
    uploads: {},
};

export const asyncFetchStories = createAsyncThunk(
    'story/fetchStories',
    async (
        {
            tourId,
            limit,
            cursorId,
            cursorDate,
            sort,
            accessKey,
        }: {
            tourId: number;
            limit: number;
            cursorId?: number;
            cursorDate?: number;
            sort?: string;
            accessKey?: string;
        }
    ) => {
        return await getStoriesByTourId(tourId, limit, cursorId, cursorDate, sort, accessKey);
    }
);

export const asyncFetchStoriesByCompoundId = createAsyncThunk(
    'story/fetchStoriesByCompoundId',
    async (
        {
            compoundId,
            limit,
            cursorId,
            cursorDate,
            sort,
            minRating,
            maxRating
        }: {
            compoundId: number;
            limit: number;
            cursorId?: number;
            cursorDate?: number;
            sort?: string;
            minRating?: number;
            maxRating?: number;
        }
    ) => {
        return await getStoriesByCompundId(compoundId, limit, cursorId, cursorDate, sort, minRating, maxRating);
    }
);

export const asyncCreateStory = createAsyncThunk(
    'story/postStory',
    async (
        {
            tourId,
            stayId,
        }: {
            tourId: number;
            stayId: number;
        }
    ) => {
        return await addStory(tourId, stayId);
    }
);

export const asyncUpdateStory = createAsyncThunk(
    'story/updateStory',
    async (updatedStory: Story) => {
        return await modifyStory(updatedStory);
    }
);

export const asyncDeleteStory = createAsyncThunk(
    'story/deleteStory',
    async (storyId: number) => {
        await deleteStoryById(storyId);
        return storyId;
    }
);

export const asyncUploadImageForStory = createAsyncThunk<
    void,
    { storyId: number; file: File; localId: string },
    { dispatch: AppDispatch; rejectValue: string }
>(
    'story/uploadImageForStory',
    async ({ storyId, file, localId }, { dispatch, rejectWithValue }) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';

        try {
            // Step 1: Indicate compression started
            dispatch(storySlice.actions.setUploadStatus({
                localId,
                status: 'compressing',
                fileExtension,
            }));

            // (Start upload link request in parallel)
            const uploadLinkPromise = getStoryUploadLink(storyId, fileExtension);

            // Step 2: Compress the image
            const compressed = await imageCompression(file, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
            });

            // Step 3: Switch to uploading status
            dispatch(storySlice.actions.setUploadStatus({
                localId,
                status: 'uploading',
                fileExtension,
            }));

            // Step 4: Wait for the upload link if not done yet
            const { url, name: remoteName } = await uploadLinkPromise;

            // Step 5: Upload to Azure Blob Storage
            await axios.put(url!, compressed, {
                headers: {
                    'Content-Type': compressed.type,
                    'x-ms-blob-type': 'BlockBlob',
                },
            });

            // Step 6: Confirm upload with backend
            const confirmed: StoryImage = await confirmStoryImageUpload(storyId, remoteName!, fileExtension);

            // Step 7: Mark upload as successful
            dispatch(storySlice.actions.setUploadStatus({
                localId,
                status: 'success',
                remoteName,
                image: confirmed,
            }));

        } catch (error: any) {
            const message = error?.message || 'Upload failed';

            dispatch(storySlice.actions.setUploadStatus({
                localId,
                status: 'error',
                errorMessage: message,
            }));

            return rejectWithValue(message);
        }
    }
);

export const storySlice = createSlice({
    name: 'story',
    initialState,
    reducers: {
        setStories: (state, action) => {
            state.stories = action.payload;
        },
        resetStories: (state) => {
            state.stories = [];
            state.nextCursorId = undefined;
            state.nextCursorDate = undefined;
            state.error = undefined;
            state.loading = true;
        },
        resetUploads: (state) => {
            state.uploads = {};
        },
        setUploadStatus: (state, action: {
            payload: {
                localId: string;
                status?: 'compressing' | 'uploading' | 'success' | 'error';
                errorMessage?: string;
                fileExtension?: string;
                remoteName?: string;
                image?: StoryImage;
            };
        }) => {
            const { localId, ...rest } = action.payload;

            state.uploads[localId] = {
                ...state.uploads[localId],
                ...rest,
            };
        },
        removeUpload: (state, action: { payload: string }) => {
            delete state.uploads[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(asyncFetchStories.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncFetchStories.fulfilled, (state, action) => {
                const payload: any = action.payload ?? { stories: [], nextCursorId: undefined, nextCursorDate: undefined };

                const { cursorId, cursorDate } = action.meta.arg;
                const isLoadMore = cursorId !== undefined || cursorDate !== undefined;

                if (isLoadMore) { // Deduplication is necessary to solve duplication edge case: Having posted a new entry that will be loaded due to nextCursor would lead to duplicate in store
                    const merged = [...state.stories, ...(payload.stories ?? [])];
                    state.stories = Array.from(new Map(merged.map((e: Story) => [e.storyId, e])).values());
                } else {
                    state.stories = payload.stories ?? [];
                }

                state.nextCursorId = payload.nextCursorId;
                state.nextCursorDate = payload.nextCursorDate;
                state.loading = false;
            })
            .addCase(asyncFetchStories.rejected, (state, action) => {
                state.loading = false;
                const msg = (action.payload as string) ?? action.error?.message ?? 'Unbekannter Fehler';
                state.error = mapErrorMessages({ errorMessage: msg });
            })

            .addCase(asyncCreateStory.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncCreateStory.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(asyncCreateStory.rejected, (state, action) => {
                state.loading = false;
                const msg = (action.payload as string) ?? action.error?.message ?? 'Unbekannter Fehler';
                state.error = mapErrorMessages({ errorMessage: msg });
            })

            .addCase(asyncUpdateStory.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncUpdateStory.fulfilled, (state, action) => {
                const updated = action.payload;
                const index = state.stories.findIndex(
                    s => s.storyId === updated.storyId
                );
                if (index !== -1) {
                    state.stories[index] = updated;
                } else {
                    state.stories.push(updated);
                }
                state.loading = false;
            })
            .addCase(asyncUpdateStory.rejected, (state, action) => {
                state.loading = false;
                const msg = (action.payload as string) ?? action.error?.message ?? 'Unbekannter Fehler';
                state.error = mapErrorMessages({ errorMessage: msg });
            })

            .addCase(asyncDeleteStory.fulfilled, (state, action) => {
                state.stories = state.stories.filter(
                    s => s.storyId !== action.payload
                );
            })
            .addCase(asyncDeleteStory.rejected, (state, action) => {
                const msg = (action.payload as string) ?? action.error?.message ?? 'Unbekannter Fehler';
                state.error = mapErrorMessages({ errorMessage: msg });
            })

            .addCase(asyncFetchStoriesByCompoundId.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncFetchStoriesByCompoundId.fulfilled, (state, action) => {
                const payload: any = action.payload ?? { stories: [], nextCursorId: undefined, nextCursorDate: undefined };

                const { cursorId, cursorDate } = action.meta.arg;
                const isLoadMore = cursorId !== undefined || cursorDate !== undefined;

                if (isLoadMore) {
                    const merged = [...state.stories, ...(payload.stories ?? [])];
                    state.stories = Array.from(new Map(merged.map((e: Story) => [e.storyId, e])).values());
                } else {
                    state.stories = payload.stories ?? [];
                }

                state.nextCursorId = payload.nextCursorId;
                state.nextCursorDate = payload.nextCursorDate;
                state.loading = false;
            })
            .addCase(asyncFetchStoriesByCompoundId.rejected, (state, action) => {
                state.loading = false;
                const msg = (action.payload as string) ?? action.error?.message ?? 'Unbekannter Fehler';
                state.error = mapErrorMessages({ errorMessage: msg });
            });
    }
});

export const { setStories, resetStories, resetUploads, removeUpload } = storySlice.actions;
export const storyReducer = storySlice.reducer;
